// ShirleyProjects.jsx - קומפוננטת גלריית פרויקטים של שירלי

import React, { useState, useEffect, useRef } from 'react';
import styles from './AllProjects.module.css';
import { shirleyProjectsConfig } from './ShirlyProjectConfig';
import { loadShirleyThumbnail, loadShirleyProjectGallery } from './ShirlyImageLoader';

// פרויקטים שהופיעו במגזינים
const projectFeaturedIn = {
  3: ['PNIM'],  // הבית האקלקטי של הודיה ועוזיה
  5: ['PNIM'],  // הבית של הילה מראשלצ
  6: ['WALLS'], // הבית של יוני וחדווי
  7: ['WALLS'], // הבית של רתם ואורטל
  8: ['WALLS'], // הבית של שיר וגלעד
};

const ShirleyProjects = () => {
  const sectionRef = useRef(null);
  const modalRef = useRef(null);
  const thumbnailRefs = useRef([]);

  // State
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectGallery, setProjectGallery] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visibleThumbnails, setVisibleThumbnails] = useState(new Set());
  const [heroOverlayOpacity, setHeroOverlayOpacity] = useState(0);

  // WhatsApp link generator
  const getWhatsAppLink = (projectTitle = '') => {
    const phoneNumber = '+972543324747'; // יש להחליף למספר האמיתי
    const message = projectTitle
      ? `היי שירלי, אהבתי את הפרויקט "${projectTitle}", אשמח לשמוע עוד על ...`
      : 'היי שירלי, הגעתי מהדף שלך ואשמח שנדבר';
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  // טעינת פרויקטים בהתחלה - כמו אצל נרקיס
  useEffect(() => {
    const loadAllThumbnails = async () => {
      const projectsWithThumbnails = await Promise.all(
        shirleyProjectsConfig.map(async (config) => {
          try {
            const thumbnail = await loadShirleyThumbnail(
              config.folderName,
              config.thumbnailIndex
            );

            return {
              ...config,
              thumbnail,
              mainImage: thumbnail,
              gallery: []
            };
          } catch (error) {
            console.error(`Failed to load thumbnail for ${config.title}:`, error);
            return {
              ...config,
              thumbnail: null,
              mainImage: null,
              gallery: []
            };
          }
        })
      );

      setProjects(projectsWithThumbnails);
    };

    loadAllThumbnails();
  }, []);

  // Intersection Observer לאנימציית כניסה של thumbnails
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setTimeout(() => {
              setVisibleThumbnails(prev => new Set([...prev, index]));
            }, index * 80);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    thumbnailRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [projects]);

  // טעינת גלריה מלאה כשפותחים פרויקט
  const loadFullGallery = async (project) => {
    try {
      const gallery = await loadShirleyProjectGallery(
        project.folderName,
        project.imageCount
      );
      setProjectGallery(gallery);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Failed to load gallery:', error);
      setProjectGallery([]);
    }
  };

  // פתיחת פרויקט
  const openProject = (project) => {
    setSelectedProject(project);
    loadFullGallery(project);
    document.body.style.overflow = 'hidden';
  };

  // סגירת פרויקט
  const closeProject = () => {
    document.body.style.overflow = '';
    setSelectedProject(null);
    setProjectGallery([]);
    setIsGalleryOpen(false);
  };

  // Scroll reset כש-modal נפתח
  useEffect(() => {
    if (selectedProject && modalRef.current) {
      modalRef.current.scrollTop = 0;
      setHeroOverlayOpacity(0);
    }
  }, [selectedProject]);

  // אפקט ערפל כשגוללים במודל
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal || !selectedProject) return;

    const handleScroll = () => {
      const scrollTop = modal.scrollTop;
      const maxScroll = 300; // נקודה שבה הערפל מלא
      const opacity = Math.min(scrollTop / maxScroll, 0.85);
      setHeroOverlayOpacity(opacity);
    };

    modal.addEventListener('scroll', handleScroll);
    return () => modal.removeEventListener('scroll', handleScroll);
  }, [selectedProject]);

  // Body scroll lock
  useEffect(() => {
    if (selectedProject) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.setAttribute('data-scroll-position', scrollY.toString());
    } else {
      const scrollPosition = document.body.getAttribute('data-scroll-position');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.removeAttribute('data-scroll-position');
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition, 10));
      }
    }
  }, [selectedProject]);

  // פתיחת lightbox
  const openGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  // ניווט בגלריה
  const nextImage = () => {
    if (projectGallery.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === projectGallery.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (projectGallery.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? projectGallery.length - 1 : prev - 1
      );
    }
  };

  // טיפול במקלדת
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isGalleryOpen) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.key === 'ArrowRight' ? prevImage() : nextImage();
        } else if (e.key === 'Escape') {
          closeGallery();
        }
      } else if (selectedProject && e.key === 'Escape') {
        closeProject();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, selectedProject, projectGallery]);

  // Cleanup
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return <>
    <section className={styles.projectsSection} ref={sectionRef} id="all-projects">
      {/* Background Decorations */}
      <div className={styles.bgDecoration}>
        <div className={styles.floatingShape1}></div>
        <div className={styles.floatingShape2}></div>
        <div className={styles.floatingShape3}></div>
        <div className={styles.gridOverlay}></div>
      </div>

      {/* Title Section */}
      <header className={styles.titleSection}>
        <h2 className={styles.sectionTitle}>
          מקבץ <span className={styles.highlight}>פרויקטים</span>
        </h2>
      </header>

      {/* Projects Grid - 4 columns */}
      <div className={styles.projectsGrid}>
        {projects.map((project, index) => (
          <article
            key={project.id}
            ref={el => thumbnailRefs.current[index] = el}
            data-index={index}
            className={`${styles.projectCard} ${visibleThumbnails.has(index) ? styles.visible : ''}`}
            onClick={() => openProject(project)}
            style={{ '--index': index }}
          >
            {/* Image Container */}
            <div className={styles.imageContainer}>
              <span className={styles.projectNumber}>
                {String(index + 1).padStart(2, '0')}
              </span>
              
              <img
                src={project.thumbnail}
                alt={project.title}
                className={styles.projectImage}
                loading="lazy"
              />
              
              {/* Overlay */}
              <div className={styles.cardOverlay}>
                <div className={styles.overlayContent}>
                  <span className={styles.viewProject}>צפייה בפרויקט</span>
                  <div className={styles.arrowCircle}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M7 17L17 7M17 7H7M17 7V17"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Category Badge */}
              <span className={styles.categoryBadge}>{project.category}</span>
            </div>

            {/* Card Content */}
            <div className={styles.cardContent}>
              <div className={styles.cardMeta}>
                <span className={styles.imageCount}>{project.imageCount} תמונות</span>
              </div>
              <h3 className={styles.cardTitle}>{project.title}</h3>
              <p className={styles.cardSubtitle}>{project.subtitle}</p>
            </div>

            {/* Featured In Ribbon */}
            {projectFeaturedIn[project.id] && (
              <div className={styles.featuredRibbon}>
                <span className={styles.ribbonLabel}>הופיע ב:</span>
                <div className={styles.ribbonMagazines}>
                  {projectFeaturedIn[project.id].join(', ')}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      {/* CTA Section */}
      <div className={styles.ctaSection}>
        <a 
          href={getWhatsAppLink()} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.ctaButton}
        >
          <span className={styles.ctaIcon}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </span>
          <span className={styles.ctaText}>לשיחת ייעוץ חינם</span>
          <span className={styles.ctaArrow}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </span>
        </a>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <div className={styles.modalBackdrop} ref={modalRef}>
          <div className={styles.modal}>
            {/* Close Button */}
            <button className={styles.closeBtn} onClick={closeProject} aria-label="סגור">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            {/* Modal Hero */}
            <div className={styles.modalHero}>
              <div className={styles.modalHeroInner}>
                <img
                  src={selectedProject.mainImage}
                  alt={selectedProject.title}
                  className={styles.modalHeroImage}
                />
                <div 
                  className={styles.modalHeroOverlay}
                  style={{ 
                    '--fog-opacity': heroOverlayOpacity,
                  }}
                ></div>
                <span className={styles.modalProjectNumber}>
                  {String(projects.findIndex(p => p.id === selectedProject.id) + 1).padStart(2, '0')}
                </span>
              </div>

              {/* Featured In Ribbon in Modal */}
              {projectFeaturedIn[selectedProject.id] && (
                <div className={styles.modalFeaturedRibbon}>
                  <span className={styles.modalRibbonLabel}>הופיע ב:</span>
                  <div className={styles.modalRibbonMagazines}>
                    {projectFeaturedIn[selectedProject.id].join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <span className={styles.modalCategory}>{selectedProject.category}</span>
                <div className={styles.modalMeta}>
                  <span>{selectedProject.imageCount} תמונות</span>
                </div>
                <h2 className={styles.modalTitle}>{selectedProject.title}</h2>
                <p className={styles.modalSubtitle}>{selectedProject.subtitle}</p>
              </div>

              {/* Project Description - NEW SECTION */}
              {selectedProject.description && (
                <div className={styles.projectDescriptionSection}>
                  <h4 className={styles.descriptionTitle}>
                    <span className={styles.descriptionTitleLine}></span>
                    על הפרויקט
                    <span className={styles.descriptionTitleLine}></span>
                  </h4>
                  <div className={styles.projectDescription}>
                    {selectedProject.description.split('\n').map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery Grid */}
              <div className={styles.galleryGridSection}>
                <h4 className={styles.gallerySectionTitle}>
                  <span className={styles.galleryTitleLine}></span>
                  גלריית תמונות
                  <span className={styles.galleryTitleLine}></span>
                </h4>

                <div className={styles.modalGallery}>
                  {projectGallery.map((img, idx) => (
                    <div
                      key={idx}
                      className={styles.galleryThumb}
                      onClick={() => openGallery(idx)}
                    >
                      <div className={styles.galleryThumbInner}>
                        <img
                          src={img}
                          alt={`${selectedProject.title} ${idx + 1}`}
                          loading="lazy"
                        />
                        <span className={styles.thumbNumber}>{String(idx + 1).padStart(2, '0')}</span>
                        <div className={styles.thumbOverlay}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* WhatsApp CTA inside Modal */}
                <div className={styles.modalCtaSection}>
                  <a
                    href={getWhatsAppLink(selectedProject.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.modalCtaButton}
                  >
                    <span className={styles.modalCtaText}>אהבתי! בואו נדבר</span>
                    <span className={styles.modalCtaArrow}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 5l-7 7 7 7"/>
                      </svg>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Gallery Lightbox */}
      {isGalleryOpen && selectedProject && projectGallery.length > 0 && (
        <div className={styles.galleryOverlay}>
          {/* Close Button */}
          <button className={styles.galleryCloseBtn} onClick={closeGallery} aria-label="סגור גלריה">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>

          {/* Main Image Viewer */}
          <div className={styles.galleryViewer}>
            <div className={styles.galleryImageFrame}>
              <img
                src={projectGallery[currentImageIndex]}
                alt=""
                className={styles.galleryMainImage}
              />
              <span className={styles.galleryImageCounter}>
                {String(currentImageIndex + 1).padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            className={`${styles.galleryNavBtn} ${styles.navPrev}`} 
            onClick={prevImage}
            aria-label="תמונה קודמת"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <button 
            className={`${styles.galleryNavBtn} ${styles.navNext}`} 
            onClick={nextImage}
            aria-label="תמונה הבאה"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          {/* Progress */}
          <div className={styles.galleryProgress}>
            <div className={styles.progressTrack}>
              {projectGallery.map((_, idx) => (
                <div
                  key={idx}
                  className={`${styles.progressDot} ${idx === currentImageIndex ? styles.progressActive : ''}`}
                  onClick={() => setCurrentImageIndex(idx)}
                />
              ))}
            </div>
            <span className={styles.progressCounter}>
              {String(currentImageIndex + 1).padStart(2, '0')} / {String(projectGallery.length).padStart(2, '0')}
            </span>
          </div>

          {/* Thumbnails Strip */}
          <div className={styles.galleryThumbsStrip}>
            {projectGallery.map((img, idx) => (
              <div
                key={idx}
                className={`${styles.thumbItem} ${idx === currentImageIndex ? styles.thumbActive : ''}`}
                onClick={() => setCurrentImageIndex(idx)}
              >
                <img src={img} alt="" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
   
</>
};

export default ShirleyProjects;